/** A run of text, flagged when it is one of the search terms. */
export interface Segment {
  text: string;
  match: boolean;
}

interface Hit {
  start: number;
  end: number;
}

/** Case-insensitive positions of every term, overlaps collapsed, in document order. */
function findHits(text: string, terms: string[]): Hit[] {
  const haystack = text.toLowerCase();
  const hits: Hit[] = [];

  for (const term of terms) {
    const needle = term.trim().toLowerCase();
    if (!needle) continue;
    let from = 0;
    for (;;) {
      const at = haystack.indexOf(needle, from);
      if (at === -1) break;
      hits.push({ start: at, end: at + needle.length });
      from = at + needle.length;
    }
  }

  hits.sort((a, b) => a.start - b.start || b.end - a.end);

  // Collapse overlaps so a nested/overlapping term can't split a segment twice
  const merged: Hit[] = [];
  for (const hit of hits) {
    const last = merged[merged.length - 1];
    if (last && hit.start <= last.end) {
      last.end = Math.max(last.end, hit.end);
    } else {
      merged.push({ ...hit });
    }
  }
  return merged;
}

/**
 * Segments `text` for display, windowed around its first match so the matched words are
 * visible rather than whatever the prompt happens to open with. Ellipses mark trimmed
 * ends. Returns null when no term matches, so callers can fall back to the raw text.
 */
export function highlightSnippet(text: string, terms: string[], radius = 60): Segment[] | null {
  if (!text) return null;
  const hits = findHits(text, terms);
  if (hits.length === 0) return null;

  const first = hits[0];
  // Bias the window forward: the match matters more than what preceded it
  const start = Math.max(0, first.start - radius);
  const end = Math.min(text.length, first.end + radius * 2);

  const segments: Segment[] = [];
  let cursor = start;
  for (const hit of hits) {
    if (hit.end <= start || hit.start >= end) continue;
    const from = Math.max(hit.start, start);
    const to = Math.min(hit.end, end);
    if (from > cursor) segments.push({ text: text.slice(cursor, from), match: false });
    segments.push({ text: text.slice(from, to), match: true });
    cursor = to;
  }
  if (cursor < end) segments.push({ text: text.slice(cursor, end), match: false });

  if (start > 0) segments.unshift({ text: '…', match: false });
  if (end < text.length) segments.push({ text: '…', match: false });
  return segments;
}
